<template>
  <div class="product-list">
    <div class="actions bg-white md:pt-10 rounded">
      <div class="container mx-auto">
        <slot name="title"></slot>

        <DescSlotRenderFn v-if="isShowDesc" />
        <search-filter v-model="filters" :product-count="filterProductCount" :isLoading="isFetchingProductsCount" @submit="handleFilterSubmit">
          <template #extra>
            <DescSlotRenderFn v-if="isShowExtraBlockDesc" />
            <slot v-if="$slots.subCategory" name="subCategory"></slot>
          </template>
        </search-filter>
      </div>
    </div>
    <div class="container mx-auto bg-white mt-6 rounded">
      <div v-if="productIds.length" class="search-result">
        <div class="products-list grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 p-5">
          <product-card v-for="productId in productIds" :key="productId" :productId="productId" />
        </div>
        <div class="navigator flex justify-center">
          <div class="description"></div>
          <div class="paging my-10">
            <nd-pagination v-model:page="pagingState.page" :page-size="pagingState.size" :total-count="totalCount" />
          </div>
        </div>
      </div>
      <list-no-result v-else>
        <span>{{ $t('listing_no_result_title') }}</span>
        <span>{{ $t('listing_no_result_desc') }}</span>
      </list-no-result>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Fragment } from 'vue'
import ListNoResult from '@/components/common/list-no-result.vue'
import ProductCard from '@/components/product/product-card.vue'
import SearchFilter from '@/components/search/filter/index.vue'

import { getProducts, getProductsCount } from '@/service/product'
import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'

import { useAppStore } from '@/store/app'
import { useProductStore } from '@/store/product'

import type { GetProductsReqParams } from '@/types/product'
import { SortRuleEnum, ProductTagIdEnum } from '@/types/product'
import type { FilterComponentProps } from '@/types/filters'
import { ModeQueryStringEnum } from '@/types/filters'
import { GtmEvent } from '@/types/gtm'
import { getQueryStringFilterState } from '@/composables/useSearchFilter'

const PAGE_SIZE = 20

const route = useRoute()
const app = useAppStore()

const productStore = useProductStore()

const regionId = computed(() => Number(route.params.rid))
const categoryId = computed(() => Number(route.params.cid || route.params.tid))
const keyword = computed(() => route.query.keyword)
const mode = computed(() => route.query.mode)

const getDefaultFilters = () => {
  const filterState = typeof route.query.filter === 'string' ? getQueryStringFilterState(route.query.filter) : null
  const isSortedByDistance = mode.value !== ModeQueryStringEnum.POPULAR_LIST_KEY && !!app.geolocation.locatedAt
  if (!filterState) {
    return { sortby: isSortedByDistance ? SortRuleEnum.DISTANCE : SortRuleEnum.POPULAR }
  }
  const { age, price, date, ...restTags } = filterState
  const [min_price, max_price] = price || []
  const [start_date, end_date] = date || []

  return trimObject({
    sortby: route.query.sortby || isSortedByDistance ? SortRuleEnum.DISTANCE : SortRuleEnum.POPULAR,
    filter_tags: trimNestedArray([age, ...Object.values(restTags)]),
    min_price,
    max_price,
    starttime: start_date && formatDate(start_date, 'yyyy-MM-dd'),
    endtime: end_date && formatDate(end_date, 'yyyy-MM-dd'),
  })
}

const filters = ref<FilterComponentProps>({ ...getDefaultFilters() })

const pagingState = reactive({
  page: 1,
  size: PAGE_SIZE,
})

const queryParams = computed(() => {
  return {
    filter_regionid: regionId.value,
    filter_keyword: keyword.value,
    size: pagingState.size,
    start: (pagingState.page - 1) * pagingState.size,
    ...(categoryId.value ? { category_id: categoryId.value } : {}),
    ...filters.value,
    ...(filters.value.filter_tags || mode.value === ModeQueryStringEnum.NEWEST_LIST_KEY
      ? {
          filter_tags: nestedArrToString([
            ...(filters.value.filter_tags ? filters.value.filter_tags : []),
            ...(mode.value === ModeQueryStringEnum.NEWEST_LIST_KEY ? [[ProductTagIdEnum.PRODUCT_NEW_RELEASE]] : []),
          ]),
        }
      : {}),
    ...(filters.value.sortby === SortRuleEnum.DISTANCE && app.geolocation.locatedAt
      ? {
          lat: app.geolocation.latitude,
          lng: app.geolocation.longitude,
        }
      : {}),
  } as GetProductsReqParams
})

const { data: productsRes, refresh: fetchProducts } = await useDualAsyncData(
  getAsyncDataKey(ASYNC_DATA_KEYS.PRODUCTS, [
    regionId.value,
    categoryId.value,
    String(keyword.value || ''),
    JSON.stringify(filters.value),
    pagingState.page,
  ]),
  async () => {
    const { data: products, total_count } = await getProducts(queryParams.value)

    productStore.handleProducts('searchProduct', products)

    if (import.meta.client && keyword.value) {
      useNuxtApp().$trackEvent(GtmEvent.SEARCH_QUERY_SENT)
    }
    return { products, total_count }
  },
  { watch: [keyword, regionId, categoryId], immediate: true }
)

const { data: filterProductCount, pending: isFetchingProductsCount } = await useAsyncData(
  getAsyncDataKey(ASYNC_DATA_KEYS.FILTER_PRODUCT_COUNT, [regionId.value, categoryId.value, String(keyword.value || '')]),
  async () => {
    const { total_count } = await getProductsCountWithDebounce(queryParams.value)
    return total_count
  },
  { watch: [queryParams] }
)

const getProductsCountWithDebounce = useDebounceFn(getProductsCount, 300)

const productIds = computed(() => productStore.listIds.searchProduct || [])
const totalCount = computed(() => productsRes.value?.total_count || 100)

const handleFilterSubmit = () => {
  pagingState.page = 1
  fetchProducts()
}

const slots = useSlots()
const DescSlotRenderFn = computed(() => h(Fragment, slots.desc ? slots.desc() : []))

const isShowDesc = computed(() => slots.desc && app.isDesktopScreen)
const isShowExtraBlockDesc = computed(() => slots.desc && (app.isMobile || app.isTablet))

watch(pagingState, () => {
  fetchProducts()
  if (import.meta.client) window.scrollTo({ top: 0, behavior: 'smooth' })
})

onMounted(async () => {
  filterProductCount.value = (await getProductsCount(queryParams.value)).total_count || 0
})
</script>

<style lang="scss" scoped>
.product-list {
}
</style>
